import { Col, Input, Row } from 'antd';
import { useRouter } from 'next/router';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';
import { extractTranslation } from '../../../helpers/formatters';
import { QuestionProps } from '../../../pages/form';
import {
  Form_FormOptionMsg,
  useListFormOptionsQuery,
  useSearchFormOptionsLazyQuery,
} from '../../../src/generated/graphql';
import { QuestionTag } from './components/question-tag';

interface MultiSelectQuestionProps extends QuestionProps {
  clinicId: string;
}

// TODO implement search
export const MultiSelectQuestion: FC<MultiSelectQuestionProps> = ({
  answer,
  onAnswer,
  question,
  clinicId,
}) => {
  const { formatMessage } = useIntl();
  const locale = useRouter().locale;

  const multiSelectQuestion = question?.multiSelect;

  const datasetId = multiSelectQuestion?.datasetId;
  const maxAnswers = multiSelectQuestion?.maxAnswers;
  const searchable = multiSelectQuestion?.searchable ?? false;

  const [searchTerm, setSearchTerm] = useState<string>('');

  const [internalOptionIds, setInternalOptionIds] = useState<string[]>([]);
  const internalOptionIdsCount = internalOptionIds.length;
  useEffect(() => {
    setInternalOptionIds((currentInternalOptionIds) => {
      if (answer?.value?.multiSelect === undefined || answer?.value?.multiSelect === null) {
        return currentInternalOptionIds;
      }

      return answer.value.multiSelect.options!.map((option) => option!.id!);
    });
  }, [answer]);

  const { data: listFormOptionsData, loading: listFormOptionsLoading } = useListFormOptionsQuery({
    skip: searchable || !datasetId,
    variables: {
      clinicId,
      datasetIds: [datasetId!],
    },
  });

  const [
    searchFormOptionsQuery,
    { data: searchFormOptionsData, loading: searchFormOptionsLoading },
  ] = useSearchFormOptionsLazyQuery();

  const [options, setOptions] = useState<Form_FormOptionMsg[]>([]);
  useEffect(() => {
    setOptions((currentOptions) => {
      if (searchable) {
        if (searchFormOptionsData?.searchFormOptions !== undefined) {
          return (
            searchFormOptionsData?.searchFormOptions?.response?.optionGroups
              ?.flatMap((optionGroup) => optionGroup?.options)
              .map((option) => option!) ?? []
          );
        }
      } else {
        if (listFormOptionsData?.listFormOptions !== undefined) {
          return (
            listFormOptionsData?.listFormOptions?.response?.options?.map((option) => option!) ?? []
          );
        }
      }
      return currentOptions;
    });
  }, [searchable, listFormOptionsData, searchFormOptionsData]);
  const searchDebounced = useDebouncedCallback((query: string) => {
    if (!datasetId || !searchable) {
      return;
    }

    searchFormOptionsQuery({
      variables: {
        clinicId,
        formDatasetId: datasetId,
        query: query,
      },
    });
  }, 400);

  useEffect(() => {
    searchDebounced(searchTerm);
  }, [searchDebounced, searchTerm]);

  function onOptionClick(clickedOptionId: string) {
    let newInternalOptionIds: string[];
    if (internalOptionIds.includes(clickedOptionId)) {
      // option is already selected -> remove
      newInternalOptionIds = internalOptionIds.filter((optionId) => optionId !== clickedOptionId);
    } else {
      newInternalOptionIds = [clickedOptionId, ...internalOptionIds];
    }

    setInternalOptionIds(newInternalOptionIds);
    onAnswer({
      multiSelect: {
        options: newInternalOptionIds.map((el) => {
          return { id: el };
        }),
      },
    });
  }

  return (
    <Row gutter={[8, 8]}>
      {searchable && (
        <Col span={24}>
          <Row>
            <Col span={24}>
              <Input
                placeholder={formatMessage({ id: 'placeholder.search' })}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
          </Row>
        </Col>
      )}
      <Col span={24}>
        <Row>
          <Col span={24}>
            {options?.map((option) => {
              const title = extractTranslation(option.title, locale) || '-';
              const subtitle = extractTranslation(option.subtitle, locale);

              const selected = internalOptionIds.includes(option.id!);

              return (
                <QuestionTag
                  key={option?.id}
                  text={title}
                  selected={selected}
                  tooltip={subtitle}
                  onClick={() => {
                    onOptionClick(option!.id!);
                  }}
                />
              );
            })}
          </Col>
        </Row>
      </Col>

      {maxAnswers && (
        <Col span={24}>
          <Row>
            <Col span={24}>{`${internalOptionIdsCount} / ${maxAnswers}`}</Col>
          </Row>
        </Col>
      )}
    </Row>
  );
};
